import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

export const Longevity = () => {
  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="chapter-longevity">
      <article className="max-w-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <header className="mb-16 animate-fade-in">
          <p className="text-xs font-sans font-medium tracking-widest uppercase text-stone-400 mb-4">
            Chapter
          </p>
          <h1 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl text-stone-800 tracking-tight leading-[1.2]">
            Longevity
          </h1>
        </header>

        <div className="prose-living space-y-8 animate-fade-in-delay-1">
          <p>
            To live a long time is a gift and a complexity. 
            It means watching the world change around you. 
            It means relationships that span decades. 
            It means plans that stretch beyond the immediate.
          </p>

          <p>
            Longevity asks different questions than urgency. 
            It invites you to think in longer arcs — 
            about health, about resources, about connection.
          </p>

          <p>
            This chapter is for those looking ahead — 
            not with fear, but with the quiet work of anticipation.
          </p>

          <div className="py-8 border-t border-b border-stone-100 my-12 space-y-6">
            <h2 className="font-serif text-xl text-stone-700">
              Long-horizon questions:
            </h2>
            <ul className="space-y-4 text-stone-600">
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>What do you hope the next ten years look like?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>What would you want to protect or preserve?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>Who would you want beside you as time passes?</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary mt-1.5">—</span>
                <span>What kind of care do you imagine needing — or giving?</span>
              </li>
            </ul>
          </div>

          <p>
            Longevity isn't about predicting. It's about preparing gently — 
            for the many seasons still to come.
          </p>

          <p>
            This space is for thinking ahead without rushing forward.
          </p>
        </div>

        <nav className="mt-16 pt-8 border-t border-stone-100 animate-fade-in-delay-2">
          <p className="text-sm font-sans text-stone-400 mb-4">Explore other chapters</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/living-book/chapters/transition"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-transition"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Transition
            </Link>
            <Link
              to="/living-book/chapters/stability"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-stability"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Stability
            </Link>
            <Link
              to="/living-book/chapters/legacy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-full text-sm font-sans text-stone-600 transition-colors"
              data-testid="chapter-link-legacy"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Legacy
            </Link>
          </div>
        </nav>
      </article>
    </div>
  );
};

export default Longevity;
